import UpiQrUpload from '../UpiQrUpload';

const ic = "w-full bg-gray-50 border border-gray-200 focus:bg-white focus:border-[#F97316] focus:ring-2 focus:ring-orange-100 rounded-xl px-4 py-2.5 text-sm outline-none transition-all";
const lc = "block text-sm font-semibold text-gray-700 mb-1.5";
const hc = "text-xs font-semibold tracking-[0.15em] uppercase text-gray-500 pt-2";

const categoryKey = (age, gender) => `${(age || '').trim()}|${(gender || '').trim()}`;

const TournamentPaymentSection = ({ f, onChange, setF }) => {
  const method = f.payment_method || 'free';
  const mode = f.fee_mode || 'single';
  const ages = f.age_categories || [];
  const genders = f.gender_categories || [];
  const category_fees = f.category_fees || {};

  const setCatFee = (age, gender, value) => {
    const key = categoryKey(age, gender);
    const next = { ...category_fees };
    if (value === '' || value === null || value === undefined) {
      delete next[key];
    } else {
      next[key] = parseInt(value, 10) || 0;
    }
    setF({ ...f, category_fees: next });
  };

  return (
    <>
      <h3 className={hc}>Registration & Payment</h3>

      <div>
        <label className={lc}>Fee Structure *</label>
        <div className="flex flex-col sm:flex-row gap-2">
          <label className={`flex-1 cursor-pointer border rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${mode === 'single' ? 'border-[#F97316] bg-orange-50 text-[#F97316]' : 'border-gray-200 bg-gray-50 text-gray-700 hover:border-gray-400'}`}>
            <input
              type="radio"
              name="fee_mode"
              value="single"
              checked={mode === 'single'}
              onChange={() => setF({ ...f, fee_mode: 'single', category_fees: null })}
              className="mr-2"
              data-testid="fee-mode-single"
            />
            Same fee for all categories
          </label>
          <label className={`flex-1 cursor-pointer border rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${mode === 'per_category' ? 'border-[#F97316] bg-orange-50 text-[#F97316]' : 'border-gray-200 bg-gray-50 text-gray-700 hover:border-gray-400'}`}>
            <input
              type="radio"
              name="fee_mode"
              value="per_category"
              checked={mode === 'per_category'}
              onChange={() => setF({ ...f, fee_mode: 'per_category' })}
              className="mr-2"
              data-testid="fee-mode-per-category"
            />
            Different fees per category
          </label>
        </div>
      </div>

      {mode === 'single' ? (
        <div>
          <label className={lc}>Registration Fee (₹) *</label>
          <input
            type="number"
            min="0"
            step="1"
            name="registration_fee"
            value={f.registration_fee ?? 0}
            onChange={(e) => setF({ ...f, registration_fee: e.target.value === '' ? 0 : parseInt(e.target.value, 10) })}
            className={ic}
            placeholder="0"
            data-testid="registration-fee-input"
          />
          <p className="text-xs text-gray-500 mt-1">Enter 0 for a free tournament.</p>
        </div>
      ) : (
        <div>
          <label className={lc}>Fee per Category (₹) *</label>
          {(ages.length === 0 || genders.length === 0) ? (
            <p className="text-sm text-orange-700 bg-orange-50 border border-orange-100 rounded-xl px-3 py-2">
              Select at least one age and one gender category first.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {ages.flatMap((a) => genders.map((g) => {
                const key = categoryKey(a, g);
                return (
                  <div key={key} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2">
                    <span className="text-sm text-gray-800 flex-1 truncate">{a} {g}</span>
                    <span className="text-sm text-gray-500">₹</span>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={category_fees[key] ?? ''}
                      onChange={(e) => setCatFee(a, g, e.target.value)}
                      className="w-24 bg-white border border-gray-200 focus:border-[#F97316] rounded-lg px-2 py-1 text-sm outline-none"
                      placeholder="0"
                      data-testid={`category-fee-${a}-${g}`}
                    />
                  </div>
                );
              }))}
            </div>
          )}
          <p className="text-xs text-gray-500 mt-2">Every category shown above needs a fee (use 0 for free).</p>
        </div>
      )}

      <div>
        <label className={lc}>Payment Method *</label>
        <div className="flex flex-col sm:flex-row gap-2">
          <label className={`flex-1 cursor-pointer border rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${method === 'free' ? 'border-[#F97316] bg-orange-50 text-[#F97316]' : 'border-gray-200 bg-gray-50 text-gray-700 hover:border-gray-400'}`}>
            <input
              type="radio"
              name="payment_method"
              value="free"
              checked={method === 'free'}
              onChange={() => setF({ ...f, payment_method: 'free' })}
              className="mr-2"
              data-testid="payment-method-free"
            />
            Free Tournament
          </label>
          <label className={`flex-1 cursor-pointer border rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${method === 'upi' ? 'border-[#F97316] bg-orange-50 text-[#F97316]' : 'border-gray-200 bg-gray-50 text-gray-700 hover:border-gray-400'}`}>
            <input
              type="radio"
              name="payment_method"
              value="upi"
              checked={method === 'upi'}
              onChange={() => setF({ ...f, payment_method: 'upi' })}
              className="mr-2"
              data-testid="payment-method-upi"
            />
            UPI Payment Required
          </label>
        </div>
      </div>

      {method === 'upi' && (
        <div className="space-y-4 border border-orange-100 bg-orange-50/50 rounded-xl p-4">
          <div>
            <label className={lc}>UPI ID *</label>
            <input
              type="text"
              name="upi_id"
              value={f.upi_id || ''}
              onChange={onChange}
              className={ic}
              placeholder="delhibasketball@oksbi"
              data-testid="upi-id-input"
            />
          </div>

          <div>
            <label className={lc}>UPI QR Code Image *</label>
            <UpiQrUpload
              value={f.upi_qr_url || ''}
              onChange={(url) => setF({ ...f, upi_qr_url: url })}
            />
          </div>

          <div>
            <label className={lc}>Payment Instructions</label>
            <textarea
              name="payment_instructions"
              value={f.payment_instructions || ''}
              onChange={onChange}
              rows={2}
              className={ic}
              placeholder="e.g. Please mention your Team Name in the payment note."
              data-testid="payment-instructions-input"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default TournamentPaymentSection;
